import delivered from '@/assets/images/product/delivered.svg';
import handle from '@/assets/images/product/handle.svg';
import rapid from '@/assets/images/product/rapid.svg';
import time from '@/assets/images/product/time.svg';
import transit1 from '@/assets/images/product/transit-1.svg';
import transit from '@/assets/images/product/transit.svg';
import { cn } from '@/lib/utils';
import Image from 'next/image';

export interface EstimateDeliveryRoot {
  __typename: string;
  code: string;
  value: string;
}
/* 
[
    {
        "__typename": "EstimateDelivery",
        "code": "tailoring_text",
        "value": "Tailoring (7-10 business days)"
    },
    {
        "__typename": "EstimateDelivery",
        "code": "tailoring_days",
        "value": "7-10"
    },
    {
        "__typename": "EstimateDelivery",
        "code": "expedited_shipping_days",
        "value": "5"
    },
    {
        "__typename": "EstimateDelivery",
        "code": "standard_shipping_days",
        "value": "15-25"
    }
] */

export const parseDays = (daysString: string | undefined) => {
  if (!daysString) {
    return {
      small: undefined,
      large: undefined,
    };
  }

  const daysArray = daysString.split('-');

  return {
    small: daysArray[0],
    large: daysArray[1] || daysArray[0],
  };
};

export function getFutureDate(daysFromNow: number): string {
  const today = new Date();
  const futureDate = new Date(today.getTime() + daysFromNow * 24 * 60 * 60 * 1000);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const month = months[futureDate.getMonth()];
  const day = futureDate.getDate();

  return `${month}.${day}`;
}

export const EstimateDeliveryNew = ({
  estimate_delivery,
  sku,
}: {
  estimate_delivery: EstimateDeliveryRoot[];
  sku: string;
}) => {
  const tailoringText = estimate_delivery.find(item => item.code === 'tailoring_text')?.value;

  const { small: tailoringDaysSmall, large: tailoringDaysLarge } = parseDays(
    estimate_delivery.find(item => item.code === 'tailoring_days')?.value,
  );
  const { small: expeditedShippingDaysSmall, large: expeditedShippingDaysLarge } = parseDays(
    estimate_delivery.find(item => item.code === 'expedited_shipping_days')?.value,
  );
  const { small: economyShippingDaysSmall, large: economyShippingDaysLarge } = parseDays(
    estimate_delivery.find(item => item.code === 'economy_shipping_days')?.value,
  );

  const expeditedShippingDateSmall = getFutureDate(
    parseInt(expeditedShippingDaysSmall || '0') + parseInt(tailoringDaysSmall || '0') + 2,
  );
  const expeditedShippingDateLarge = getFutureDate(
    parseInt(expeditedShippingDaysLarge || '0') + parseInt(tailoringDaysLarge || '0') + 2,
  );
  const economyShippingDateSmall = getFutureDate(
    parseInt(economyShippingDaysSmall || '0') + parseInt(tailoringDaysSmall || '0') + 4,
  );
  const economyShippingDateLarge = getFutureDate(
    parseInt(economyShippingDaysLarge || '0') + parseInt(tailoringDaysLarge || '0') + 6,
  );

  const doNotHaveStandardShipping = economyShippingDaysSmall === undefined && economyShippingDaysLarge === undefined;
  const haveStandardShipping = !doNotHaveStandardShipping;

  const stepTitleClass = 'row-span-1 text-80583F';

  const tailoringTextSplit = tailoringText?.split('(');

  const deliveryStepsClass =
    'flex justify-between bg-bgtheme min-h-[72px] text-[13px] p-[8px] lg_px-[30px] lg_py-[8px] mt-2';

  return (
    <div
      className={cn('lg_border-y lg_border-solid lg_border-E7DED5', 'py-[15px] lg_mt-5', 'px-2.5 lg_px-0', 'bg-white')}
    >
      <div className={'font-paytone mb-[10px] text-[17px] leading-5'}>Estimate Delivery (Standard & Custom Size)</div>
      <div className={'flex flex-col justify-between gap-2 text-[15px] lg_flex-row lg_gap-0'}>
        <span>
          <Image
            className={'mr-2 inline-block'}
            src={rapid}
            alt=''
          />
          Expedited Shipping: {expeditedShippingDateSmall} - {expeditedShippingDateLarge}
        </span>
        {haveStandardShipping && <span className={'hidden h-[14px] w-px bg-[#e7ded5] lg_inline-block'} />}
        {haveStandardShipping && (
          <span>
            <Image
              className={'mr-2 inline-block'}
              src={time}
              alt=''
            />
            Economy Shipping: get it {economyShippingDateSmall} - {economyShippingDateLarge}
          </span>
        )}
      </div>

      <div className={deliveryStepsClass}>
        <div className={'flex max-w-[100px] flex-col items-center justify-center gap-1 lg_max-w-[150px] lg_flex-row'}>
          <Image
            className='row-span-3 inline-flex sm_block'
            src={handle}
            alt=''
          />
          <div className='py-1 text-center text-80583F'>
            <p className={stepTitleClass}>{tailoringTextSplit?.[0]}</p>
            <p className={stepTitleClass}>{'(' + tailoringTextSplit?.[1]}</p>
          </div>
        </div>

        <span className={'self-center'}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='17'
            height='16'
            fill='none'
            viewBox='0 0 17 16'
          >
            <path
              fill='#80583F'
              fillRule='evenodd'
              d='M7.5 9v7h2V9h7V7h-7V0h-2v7h-7v2z'
              clipRule='evenodd'
            />
          </svg>
        </span>
        <div className={cn('flex flex-col items-center justify-center text-80583F')}>
          <div className='flex items-center gap-1'>
            <Image
              className={'row-span-3 hidden sm_block lg_inline-flex'}
              src={transit}
              alt=''
            />
            <div className='py-1 text-center'>
              <p className={stepTitleClass}>Expedited Shipping</p>
              <p className={stepTitleClass}>
                (
                {expeditedShippingDaysSmall === expeditedShippingDaysLarge
                  ? expeditedShippingDaysSmall
                  : `${expeditedShippingDaysSmall}-${expeditedShippingDaysLarge}`}{' '}
                business days)
              </p>
            </div>
          </div>
          {haveStandardShipping && <div className='my-[10px] h-px w-full bg-[#80583f]' />}
          {haveStandardShipping && (
            <div className='flex items-center gap-1'>
              <Image
                className={'row-span-3 hidden sm_block lg_inline-flex'}
                src={transit1}
                alt=''
              />
              <div className={'py-1 text-center'}>
                <p className={stepTitleClass}>Economy Shipping</p>
                <p className={stepTitleClass}>
                  (
                  {economyShippingDaysSmall === economyShippingDaysLarge
                    ? economyShippingDaysSmall
                    : `${economyShippingDaysSmall}-${economyShippingDaysLarge}`}{' '}
                  business days)
                </p>
              </div>
            </div>
          )}
        </div>
        <span className={'self-center'}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='17'
            height='10'
            fill='none'
            viewBox='0 0 17 10'
          >
            <path
              fill='#80583F'
              fillRule='evenodd'
              d='M16.5 2H.5V0h16zM16.5 10H.5V8h16z'
              clipRule='evenodd'
            />
          </svg>
        </span>
        <div className={'flex flex-col items-center justify-center lg_flex-row'}>
          <Image
            className={'row-span-3 inline-flex sm_block'}
            src={delivered}
            alt=''
          />
          <div className={'py-1 text-center'}>
            <p className={stepTitleClass}>Delivered</p>
          </div>
        </div>
      </div>
    </div>
  );
};
